import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import {
    ChevronLeft,
    ChevronRight,
    Heart,
    MessageCircle,
    Monitor,
    Pencil,
    Share2,
    Smartphone,
    Star,
    X,
    Search,
    Menu,
    Plus
} from 'lucide-react';
import { FormatOutlineView, FormatCopywritingView } from '../shared/ContentFormatters';

/* ══════════════════════════════════════════════════════════
   ContentPreviewModal — 通用内容预览弹窗
   可在「排版工坊」和「历史详情」中复用
   
   修改后：去除外层弹窗容器，采用独立卡片浮层 + 悬浮控制栏设计
══════════════════════════════════════════════════════════ */

const ContentPreviewModal = ({
    open,
    onClose,
    htmlContent = '',
    plainText = '',
    title = '',
    channel = 'wechat',
    authorName = 'ContentOS',
    images = [],
    tags = [],
    isOutline = false,
}) => {
    const [deviceMode, setDeviceMode] = useState('web');
    const [imgIdx, setImgIdx] = useState(0);
    const [xhsMobileView, setXhsMobileView] = useState('note'); // 'note' | 'cover'
    const [showXhsWebIndicatorPill, setShowXhsWebIndicatorPill] = useState(false);
    const xhsWebIndicatorTimerRef = useRef(null);

    const isXhsWebPreview = open && channel === 'xiaohongshu' && deviceMode === 'web' && images.length > 1;

    const showXhsWebIndicator = () => {
        setShowXhsWebIndicatorPill(true);
        if (xhsWebIndicatorTimerRef.current) {
            window.clearTimeout(xhsWebIndicatorTimerRef.current);
        }
    };

    const hideXhsWebIndicator = (delay = 500) => {
        if (xhsWebIndicatorTimerRef.current) {
            window.clearTimeout(xhsWebIndicatorTimerRef.current);
        }
        xhsWebIndicatorTimerRef.current = window.setTimeout(() => {
            setShowXhsWebIndicatorPill(false);
        }, delay);
    };

    useEffect(() => {
        return () => {
            if (xhsWebIndicatorTimerRef.current) {
                window.clearTimeout(xhsWebIndicatorTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (!isXhsWebPreview) {
            setShowXhsWebIndicatorPill(false);
            if (xhsWebIndicatorTimerRef.current) {
                window.clearTimeout(xhsWebIndicatorTimerRef.current);
            }
        }
    }, [isXhsWebPreview]);

    if (!open) return null;

    const channelLabel = channel === 'wechat' ? '公众号' : channel === 'xiaohongshu' ? '小红书' : 'Web';

    const xhsTextContent = isOutline
        ? <FormatOutlineView text={plainText} />
        : <FormatCopywritingView text={plainText} />;
    const xhsPhoneScale = 'min(calc((100vh - 140px) / 812), calc((100vw - 300px) / 375), 0.96)';

    /* ── WeChat Mobile ── */
    const renderWechatMobile = () => (
        <div className="flex h-full flex-col rounded-[36px] border-[10px] border-[#3a3a3c] bg-white shadow-[0_20px_60px_-10px_rgba(0,0,0,0.5)] !w-[360px] mx-auto overflow-hidden ring-1 ring-white/10">
            {/* Notch */}
            <div className="flex h-7 shrink-0 items-center justify-center bg-[#3a3a3c]">
                <span className="h-1.5 w-20 rounded-full bg-white/20" />
            </div>
            {/* WeChat header */}
            <div className="shrink-0 bg-[#07c160] px-4 py-3 text-center text-[15px] font-semibold text-white tracking-wide shadow-sm z-10">公众号</div>
            {/* Scrollable content area */}
            <div className="flex-1 overflow-y-auto p-5 no-scrollbar bg-[#f2f2f2]">
                <div className="bg-white rounded-lg p-4 min-h-full">
                    {htmlContent ? (
                        <div dangerouslySetInnerHTML={{ __html: htmlContent }} className="prose prose-sm prose-img:rounded-md" />
                    ) : (
                        <div className="flex h-full items-center justify-center text-sm text-gray-400">暂无内容</div>
                    )}
                </div>
            </div>
            {/* Home indicator */}
            <div className="flex h-6 shrink-0 items-center justify-center bg-white pt-1">
                <span className="h-1 w-[120px] rounded-full bg-gray-300" />
            </div>
        </div>
    );

    /* ── WeChat Web ── */
    const renderWechatWeb = () => (
        <div className="mx-auto h-full w-full overflow-y-auto rounded-[24px] bg-white shadow-[0_20px_60px_-10px_rgba(0,0,0,0.5)] overflow-hidden flex flex-col">
            <div className="shrink-0 bg-[#f8f9fa] border-b border-gray-100 px-8 py-4 flex items-center gap-3">
                <div className="h-4 w-4 rounded-full bg-[#07c160]/20 flex items-center justify-center">
                    <div className="h-2 w-2 rounded-full bg-[#07c160]" />
                </div>
                <span className="text-[15px] font-semibold text-gray-700">公众号网页预览</span>
            </div>
            <div className="flex-1 overflow-y-auto px-10 py-10 md:px-16 no-scrollbar">
                <div className="mx-auto max-w-3xl">
                    {htmlContent ? (
                        <div
                            className="prose prose-slate max-w-none prose-img:rounded-xl prose-a:text-[#07c160]"
                            dangerouslySetInnerHTML={{ __html: htmlContent }}
                            style={{ lineHeight: '1.8', fontSize: '17px' }}
                        />
                    ) : (
                        <div className="flex items-center justify-center py-20 text-sm text-gray-300">暂无内容</div>
                    )}
                </div>
            </div>
        </div>
    );

    /* ── XHS Mobile ── */
    const renderXHSMobile = () => (
        <div className="relative flex h-full w-full items-center justify-center overflow-visible py-8">
            <div
                className="relative shrink-0"
                style={{ width: 375, height: 812, transform: `scale(${xhsPhoneScale})`, transformOrigin: 'center center' }}
            >
                {/* Toggle Note / Cover - mobile fallback (top) */}
                <div className="absolute -top-[58px] left-1/2 z-[60] flex -translate-x-1/2 items-center gap-2 lg:hidden">
                    <button
                        type="button"
                        onClick={() => setXhsMobileView('note')}
                        className={`rounded-full px-4 py-2 text-[13px] font-semibold whitespace-nowrap shadow-lg transition-all duration-300 ring-1 ${xhsMobileView === 'note' ? 'bg-white text-gray-900 ring-gray-900/5' : 'bg-gray-900/55 backdrop-blur-md text-white/85 ring-white/10 hover:bg-gray-800/70 hover:text-white'}`}
                    >
                        笔记视图
                    </button>
                    <button
                        type="button"
                        onClick={() => setXhsMobileView('cover')}
                        className={`rounded-full px-4 py-2 text-[13px] font-semibold whitespace-nowrap shadow-lg transition-all duration-300 ring-1 ${xhsMobileView === 'cover' ? 'bg-white text-gray-900 ring-gray-900/5' : 'bg-gray-900/55 backdrop-blur-md text-white/85 ring-white/10 hover:bg-gray-800/70 hover:text-white'}`}
                    >
                        发现流视图
                    </button>
                </div>

                {/* Toggle Note / Cover - desktop (right side) */}
                <div className="absolute left-[calc(100%+18px)] top-1/2 z-[60] hidden -translate-y-1/2 flex-col gap-3 lg:flex">
                    <button
                        type="button"
                        onClick={() => setXhsMobileView('note')}
                        className={`rounded-full px-5 py-2.5 text-[14px] font-semibold whitespace-nowrap shadow-xl transition-all duration-300 ring-1 ${xhsMobileView === 'note' ? 'bg-white text-gray-900 ring-gray-900/5' : 'bg-gray-900/55 backdrop-blur-md text-white/85 ring-white/10 hover:bg-gray-800/70 hover:text-white'}`}
                    >
                        笔记视图
                    </button>
                    <button
                        type="button"
                        onClick={() => setXhsMobileView('cover')}
                        className={`rounded-full px-5 py-2.5 text-[14px] font-semibold whitespace-nowrap shadow-xl transition-all duration-300 ring-1 ${xhsMobileView === 'cover' ? 'bg-white text-gray-900 ring-gray-900/5' : 'bg-gray-900/55 backdrop-blur-md text-white/85 ring-white/10 hover:bg-gray-800/70 hover:text-white'}`}
                    >
                        发现流视图
                    </button>
                </div>

                {/* iPhone Frame */}
                <div className="relative h-full w-full overflow-hidden rounded-[50px] border-[8px] border-[#3a3a3c] bg-white shadow-[0_20px_60px_-10px_rgba(0,0,0,0.5)] ring-1 ring-white/10">
                    {/* Notch (Dynamic Island) */}
                    <div className="absolute left-1/2 top-2 z-40 h-[28px] w-[116px] -translate-x-1/2 rounded-full bg-black shadow-inner" />
                    <div className="absolute left-1/2 top-[13px] z-50 h-[10px] w-[10px] -translate-x-1/2 rounded-full bg-[#111] translate-x-[36px] opacity-60" />

                    {/* Status Bar */}
                    <div className="relative z-30 flex h-[50px] shrink-0 items-end justify-between px-6 pb-2.5 bg-transparent pointer-events-none">
                        <span className={`text-[14px] font-semibold ${xhsMobileView === 'note' ? 'text-gray-900' : 'text-gray-900'}`}>9:41</span>
                        <div className="flex items-center gap-1.5 pt-0.5">
                            {/* Signal */}
                            <div className="flex items-end gap-[1.5px] h-2.5">
                                <div className={`w-[2.5px] h-1.5 rounded-sm ${xhsMobileView === 'note' ? 'bg-gray-900' : 'bg-gray-900'}`}></div>
                                <div className={`w-[2.5px] h-2 rounded-sm ${xhsMobileView === 'note' ? 'bg-gray-900' : 'bg-gray-900'}`}></div>
                                <div className={`w-[2.5px] h-2.5 rounded-sm ${xhsMobileView === 'note' ? 'bg-gray-900' : 'bg-gray-900'}`}></div>
                                <div className={`w-[2.5px] h-3 rounded-sm ${xhsMobileView === 'note' ? 'bg-gray-900' : 'bg-gray-900'}`}></div>
                            </div>
                            {/* Wifi */}
                            <svg className={`h-[15px] w-[15px] ${xhsMobileView === 'note' ? 'text-gray-900' : 'text-gray-900'}`} viewBox="0 0 24 24" fill="currentColor"><path d="M12 3C7.3 3 3.1 4.6 0 7.3l12 14.4L24 7.3C20.9 4.6 16.7 3 12 3z" /></svg>
                            {/* Battery */}
                            <div className={`relative flex h-[11px] w-[22px] items-center justify-center rounded-[3px] border p-[1px] ml-0.5 ${xhsMobileView === 'note' ? 'border-gray-900' : 'border-gray-900'}`}>
                                <div className={`h-full w-full rounded-[1.5px] ${xhsMobileView === 'note' ? 'bg-gray-900' : 'bg-gray-900'}`} />
                                <div className={`absolute -right-[3px] h-1 w-[2px] rounded-r-[1px] ${xhsMobileView === 'note' ? 'bg-gray-900' : 'bg-gray-900'}`} />
                            </div>
                        </div>
                    </div>

                    {xhsMobileView === 'note' ? (
                        /* --- NOTE PREVIEW --- */
                        <div className="flex flex-col h-[calc(100%-50px-34px)] bg-white absolute inset-0 pt-[50px] z-20">
                            {/* Header */}
                            <div className="flex shrink-0 items-center justify-between px-3 py-2.5">
                                <div className="flex items-center gap-2">
                                    <ChevronLeft className="h-7 w-7 text-[#333] -ml-2 cursor-pointer" strokeWidth={2.5} />
                                    <div className="flex items-center gap-2">
                                        <div className="h-[34px] w-[34px] rounded-full bg-orange-100 flex items-center justify-center text-sm shadow-inner ring-1 ring-black/5 object-cover overflow-hidden">
                                            <img src="https://api.dicebear.com/7.x/avataaars/svg?seed=Content" alt="avatar" className="h-full w-full object-cover" />
                                        </div>
                                        <span className="text-[15px] font-semibold text-[#333] -mt-0.5 max-w-[140px] truncate">{authorName}</span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <button type="button" className="rounded-full border border-[#ff2442] px-4 py-[3px] text-[14px] font-bold text-[#ff2442]">关注</button>
                                    <Share2 className="h-[22px] w-[22px] text-[#333] cursor-pointer" strokeWidth={2.5} />
                                </div>
                            </div>

                            {/* Content Scroll */}
                            <div className="flex-1 overflow-y-auto no-scrollbar relative bg-white">
                                {/* Images */}
                                {images.length > 0 && (
                                    <div className="relative aspect-[3/4] bg-[#f8f8f8]">
                                        <img src={images[imgIdx]} alt={title} className="h-full w-full object-cover" />
                                        {images.length > 1 && (
                                            <div className="absolute bottom-4 left-0 right-0 flex items-center justify-center gap-1.5">
                                                {images.map((_, i) => (
                                                    <button
                                                        key={`dot_${i}`}
                                                        type="button"
                                                        onClick={() => setImgIdx(i)}
                                                        className={`rounded-full transition-all ${i === imgIdx ? 'h-[6px] w-[6px] bg-[#ff2442]' : 'h-[5px] w-[5px] bg-white/60 drop-shadow-sm'}`}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Text Content */}
                                <div className="px-4 pt-3 pb-8">
                                    <h3 className="text-[18px] font-bold leading-[1.45] text-[#333] mb-3">{title}</h3>
                                    <div className="text-[15px] leading-[1.7] text-[#333] whitespace-pre-wrap font-normal">{xhsTextContent}</div>
                                    {tags && tags.length > 0 && (
                                        <div className="flex flex-wrap gap-x-2 gap-y-1.5 mt-5">
                                            {tags.map((tag) => (
                                                <span key={tag} className="text-[16px] font-medium text-[#13386C]">#{String(tag).replace(/^#/, '')} </span>
                                            ))}
                                        </div>
                                    )}
                                    <div className="mt-5 text-[12px] text-gray-400 font-medium">01-01 广东</div>
                                </div>
                            </div>

                            {/* Bottom Bar */}
                            <div className="flex shrink-0 items-center justify-between border-t border-gray-100 bg-white px-4 py-2 pb-1 relative z-30">
                                <div className="flex h-[34px] w-[140px] items-center gap-1.5 rounded-full bg-[#f5f5f5] px-4 font-medium">
                                    <Pencil className="h-3.5 w-3.5 text-gray-400" strokeWidth={2.5} />
                                    <span className="text-[14px] text-gray-400">说点什么...</span>
                                </div>
                                <div className="flex items-center gap-[18px]">
                                    <div className="flex items-center gap-[3px] text-[#333]"><Heart className="h-[22px] w-[22px] stroke-[1.8px]" /><span className="text-[13px] font-medium">点赞</span></div>
                                    <div className="flex items-center gap-[3px] text-[#333]"><Star className="h-[22px] w-[22px] stroke-[1.8px]" /><span className="text-[13px] font-medium">收藏</span></div>
                                    <div className="flex items-center gap-[3px] text-[#333]"><MessageCircle className="h-[22px] w-[22px] stroke-[1.8px]" /><span className="text-[13px] font-medium">评论</span></div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        /* --- COVER PREVIEW --- */
                        <div className="flex flex-col h-[calc(100%-0px)] bg-[#F5F5F5] absolute inset-0 pt-[50px] z-20">
                            {/* Tabs Header */}
                            <div className="bg-[#F5F5F5] px-4 pb-2 pt-1 flex items-center justify-between z-10 shrink-0">
                                <Menu className="h-6 w-6 text-gray-800" strokeWidth={2} />
                                <div className="flex items-center gap-7">
                                    <span className="text-[16px] text-gray-500 font-medium tracking-wide">关注</span>
                                    <span className="text-[16px] text-[#333] font-bold tracking-wide relative flex flex-col items-center">
                                        发现
                                        <span className="absolute -bottom-1.5 h-[3px] w-6 bg-[#ff2442] rounded-full"></span>
                                    </span>
                                    <span className="text-[16px] text-gray-500 font-medium tracking-wide">附近</span>
                                </div>
                                <Search className="h-6 w-6 text-gray-800" strokeWidth={2} />
                            </div>

                            {/* Categories */}
                            <div className="bg-[#F5F5F5] px-4 py-2.5 flex items-center gap-[22px] overflow-x-auto no-scrollbar z-10 text-[14px] text-[#666] font-medium shrink-0">
                                <span className="text-[#333] font-bold shrink-0 bg-gray-200/60 px-3 py-1 rounded-full text-[13px]">推荐</span>
                                <span className="shrink-0">直播</span>
                                <span className="shrink-0">短剧</span>
                                <span className="shrink-0">穿搭</span>
                                <span className="shrink-0">旅行</span>
                                <span className="shrink-0">动漫</span>
                                <span className="shrink-0">游戏</span>
                            </div>

                            {/* Waterfall Feed */}
                            <div className="flex-1 overflow-y-auto w-full px-2 pt-1 flex gap-2 pb-20 no-scrollbar relative z-0">
                                {/* Left Column */}
                                <div className="flex-1 flex flex-col gap-2">
                                    {/* The User's Note */}
                                    <div className="bg-white rounded-[6px] overflow-hidden shadow-sm">
                                        {images.length > 0 ? (
                                            <img src={images[0]} alt="cover" className="w-full object-cover aspect-[3/4]" />
                                        ) : (
                                            <div className="w-full aspect-[3/4] bg-[#eaeaea]" />
                                        )}
                                        <div className="px-2.5 py-3">
                                            <h4 className="text-[14px] font-medium text-[#333] leading-[1.4] line-clamp-2">{title || "此处为笔记标题..."}</h4>
                                            <div className="mt-2.5 flex items-center justify-between">
                                                <div className="flex items-center gap-1.5">
                                                    <div className="h-4 w-4 rounded-full bg-orange-100 overflow-hidden ring-1 ring-black/5">
                                                        <img src="https://api.dicebear.com/7.x/avataaars/svg?seed=Content" alt="avatar" className="h-full w-full object-cover" />
                                                    </div>
                                                    <span className="text-[11px] text-[#666] truncate max-w-[70px]">{authorName}</span>
                                                </div>
                                                <div className="flex items-center gap-[2px] text-[#666]">
                                                    <Heart className="h-3 w-3 stroke-[1.5px]" />
                                                    <span className="text-[11px] font-medium tracking-tighter">0</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Dummy Note 1 */}
                                    <div className="bg-white rounded-[6px] overflow-hidden shadow-sm">
                                        <div className="w-full h-48 bg-[#e8e8e8]" />
                                        <div className="px-2.5 py-3">
                                            <h4 className="text-[14px] font-medium text-[#333] leading-[1.4]">示例笔记标题2</h4>
                                            <div className="mt-2.5 flex items-center justify-between">
                                                <div className="flex items-center gap-1.5">
                                                    <div className="h-4 w-4 rounded-full bg-gray-200"></div>
                                                    <span className="text-[11px] text-[#666]">用户名</span>
                                                </div>
                                                <div className="flex items-center gap-[2px] text-[#666]">
                                                    <Heart className="h-3 w-3 stroke-[1.5px]" />
                                                    <span className="text-[11px] font-medium tracking-tighter">0</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Right Column */}
                                <div className="flex-1 flex flex-col gap-2">
                                    {/* Dummy Note 2 */}
                                    <div className="bg-white rounded-[6px] overflow-hidden shadow-sm">
                                        <div className="w-full h-[140px] bg-[#e2e2e2]" />
                                        <div className="px-2.5 py-3">
                                            <h4 className="text-[14px] font-medium text-[#333] leading-[1.4]">示例笔记标题1</h4>
                                            <div className="mt-2.5 flex items-center justify-between">
                                                <div className="flex items-center gap-1.5">
                                                    <div className="h-4 w-4 rounded-full bg-gray-200"></div>
                                                    <span className="text-[11px] text-[#666]">用户名</span>
                                                </div>
                                                <div className="flex items-center gap-[2px] text-[#666]">
                                                    <Heart className="h-3 w-3 stroke-[1.5px]" />
                                                    <span className="text-[11px] font-medium tracking-tighter">0</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Dummy Note 3 */}
                                    <div className="bg-white rounded-[6px] overflow-hidden shadow-sm">
                                        <div className="w-full h-[220px] bg-[#dbdbdb]" />
                                        <div className="px-2.5 py-3">
                                            <h4 className="text-[14px] font-medium text-[#333] leading-[1.4]">示例笔记标题3</h4>
                                            <div className="mt-2.5 flex items-center justify-between">
                                                <div className="flex items-center gap-1.5">
                                                    <div className="h-4 w-4 rounded-full bg-gray-200"></div>
                                                    <span className="text-[11px] text-[#666]">用户名</span>
                                                </div>
                                                <div className="flex items-center gap-[2px] text-[#666]">
                                                    <Heart className="h-3 w-3 stroke-[1.5px]" />
                                                    <span className="text-[11px] font-medium tracking-tighter">0</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Bottom Nav Bar */}
                            <div className="absolute bottom-0 left-0 right-0 flex shrink-0 items-center justify-between bg-white/95 backdrop-blur-md px-7 py-2 pb-[30px] border-t border-gray-100 z-30">
                                <span className="text-[15px] font-bold text-[#333]">首页</span>
                                <span className="text-[15px] font-medium text-[#999]">市集</span>
                                <div className="flex h-[34px] w-[46px] items-center justify-center rounded-[10px] bg-[#ff2442] text-white shadow-sm ring-1 ring-[#ff2442]/20">
                                    <Plus className="h-[22px] w-[22px]" strokeWidth={3} />
                                </div>
                                <span className="text-[15px] font-medium text-[#999]">消息</span>
                                <span className="text-[15px] font-medium text-[#999]">我</span>
                            </div>
                        </div>
                    )}

                    {/* Home Indicator */}
                    <div className="absolute bottom-1.5 left-0 right-0 flex h-[24px] items-center justify-center bg-transparent z-40 pointer-events-none">
                        <span className={`h-[5px] w-[134px] rounded-full ${xhsMobileView === 'note' ? 'bg-[#3a3a3c]' : 'bg-[#3a3a3c]'}`} />
                    </div>
                </div>
            </div>
        </div>
    );

    /* ── XHS Web ── */
    const renderXHSWeb = () => (
        <div className="mx-auto flex h-full w-full overflow-hidden rounded-[24px] bg-white shadow-[0_20px_60px_-10px_rgba(0,0,0,0.5)]">
            {/* 左侧：图片 */}
            <div
                className="relative flex basis-[52%] shrink-0 items-stretch justify-center overflow-hidden bg-[#f5f5f5] border-r border-gray-100"
                onMouseEnter={showXhsWebIndicator}
                onMouseMove={showXhsWebIndicator}
                onMouseLeave={() => {
                    if (!isXhsWebPreview) return;
                    hideXhsWebIndicator(450);
                }}
            >
                {images.length > 0 ? (
                    <>
                        {/* 模糊背景 */}
                        <div
                            className="absolute inset-0 bg-cover bg-center blur-2xl opacity-40 scale-110"
                            style={{ backgroundImage: `url(${images[imgIdx]})` }}
                        />
                        <div className="absolute inset-0 bg-black/5" />

                        <img src={images[imgIdx]} alt={title} className="relative z-10 h-full w-full object-cover" />

                        {images.length > 1 && imgIdx > 0 && (
                            <button type="button" onClick={() => setImgIdx(i => i - 1)} className="absolute left-6 top-1/2 z-20 -translate-y-1/2 rounded-full bg-black/20 p-2.5 text-white hover:bg-black/40 backdrop-blur-sm transition-colors"><ChevronLeft className="h-6 w-6" /></button>
                        )}
                        {images.length > 1 && imgIdx < images.length - 1 && (
                            <button type="button" onClick={() => setImgIdx(i => i + 1)} className="absolute right-6 top-1/2 z-20 -translate-y-1/2 rounded-full bg-black/20 p-2.5 text-white hover:bg-black/40 backdrop-blur-sm transition-colors"><ChevronRight className="h-6 w-6" /></button>
                        )}
                        {images.length > 1 && (
                            <div
                                className={`absolute bottom-6 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2 rounded-full bg-black/35 px-3 py-2 backdrop-blur-md ring-1 ring-white/20 shadow-[0_10px_24px_rgba(0,0,0,0.35)] transition-all duration-300 ${showXhsWebIndicatorPill
                                    ? 'opacity-100 translate-y-0'
                                    : 'opacity-0 translate-y-1 pointer-events-none'
                                    }`}
                            >
                                {images.map((_, i) => (
                                    <button
                                        key={`d_${i}`}
                                        type="button"
                                        onClick={() => setImgIdx(i)}
                                        className={`rounded-full transition-all ${i === imgIdx
                                            ? 'h-2 w-2 bg-white shadow-sm scale-100'
                                            : 'h-1.5 w-1.5 bg-white/55 hover:bg-white/80 scale-95'
                                            }`}
                                    />
                                ))}
                            </div>
                        )}
                    </>
                ) : (
                    <div className="relative z-10 flex h-full w-full items-center justify-center text-sm text-gray-400">暂无图片</div>
                )}
            </div>
            {/* 右侧：内容 (白色底板，独立圆角与轻微左侧阴影隔离图片区) */}
            <div className="relative z-20 flex min-w-[360px] basis-[48%] flex-col bg-white">
                {/* 作者栏 */}
                <div className="flex shrink-0 items-center justify-between px-6 py-5 z-10 bg-white">
                    <div className="flex items-center gap-2.5">
                        <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center shadow-inner object-cover overflow-hidden">
                            <img src="https://api.dicebear.com/7.x/avataaars/svg?seed=Content" alt="avatar" className="h-full w-full object-cover" />
                        </div>
                        <div className="text-[15px] font-semibold text-[#333] tracking-wide">{authorName}</div>
                    </div>
                    <button type="button" className="rounded-full bg-[#ff2442] px-5 py-1.5 text-[14px] font-medium text-white shadow-sm hover:bg-[#e61e38] transition-colors">关注</button>
                </div>
                {/* 可滚动内容 */}
                <div className="flex-1 overflow-y-auto px-6 py-2 no-scrollbar">
                    <h3 className="text-[18px] font-bold leading-snug text-[#333] mb-3">{title}</h3>
                    <div className="text-[15px] leading-[1.8] text-[#333]" style={{ whiteSpace: 'pre-wrap' }}>{xhsTextContent}</div>
                    {tags && tags.length > 0 && (
                        <div className="mt-4 flex flex-wrap gap-x-2 gap-y-1.5">
                            {tags.map((tag) => (
                                <span key={tag} className="text-[16px] font-medium text-[#13386C] hover:opacity-80 cursor-pointer">#{String(tag).replace(/^#/, '')} </span>
                            ))}
                        </div>
                    )}
                    <div className="mt-6 text-[14px] text-gray-400 font-medium">01-01 广东</div>

                    {/* 评论区占位 */}
                    <div className="mt-8 border-t border-gray-50 pt-6">
                        <div className="text-[14px] text-gray-500 font-medium mb-4">共 0 条评论</div>
                        <div className="flex items-center justify-center py-10">
                            <div className="text-[14px] text-gray-400">还没有评论，快来抢沙发~</div>
                        </div>
                    </div>
                </div>
                {/* 底部互动栏 */}
                <div className="flex shrink-0 items-center gap-4 border-t border-gray-100 px-6 py-4 bg-white z-10">
                    <div className="text-[22px] grayscale opacity-50 hover:grayscale-0 hover:opacity-100 transition-all cursor-pointer">😊</div>
                    <div className="flex-1 flex items-center gap-2 rounded-full bg-[#f5f5f5] px-4 py-2">
                        <span className="text-[13px] text-gray-500">说点什么...</span>
                    </div>
                    <div className="flex items-center gap-5 pl-1">
                        <div className="flex items-center gap-1.5 text-gray-500 hover:text-[#ff2442] transition-colors cursor-pointer"><Heart className="h-5 w-5 stroke-[1.5px]" /><span className="text-[14px] font-medium">0</span></div>
                        <div className="flex items-center gap-1.5 text-gray-500 hover:text-yellow-500 transition-colors cursor-pointer"><Star className="h-5 w-5 stroke-[1.5px]" /><span className="text-[14px] font-medium">0</span></div>
                        <div className="flex items-center gap-1.5 text-gray-500 hover:text-gray-900 transition-colors cursor-pointer"><MessageCircle className="h-5 w-5 stroke-[1.5px]" /><span className="text-[14px] font-medium">0</span></div>
                        <div className="flex items-center text-gray-400 hover:text-gray-900 transition-colors cursor-pointer ml-1"><Share2 className="h-5 w-5 stroke-[1.5px]" /></div>
                    </div>
                </div>
            </div>
        </div>
    );

    /* ── WordPress Web ── */
    const renderWordPressWeb = () => (
        <div className="mx-auto h-full w-full overflow-y-auto rounded-[24px] bg-white shadow-[0_20px_60px_-10px_rgba(0,0,0,0.5)] overflow-hidden flex flex-col">
            <div className="shrink-0 bg-[#f8f9fa] border-b border-gray-100 px-8 py-4 flex items-center gap-3">
                <div className="flex items-center gap-1.5">
                    <div className="h-3 w-3 rounded-full bg-red-400" />
                    <div className="h-3 w-3 rounded-full bg-yellow-400" />
                    <div className="h-3 w-3 rounded-full bg-green-400" />
                </div>
                <span className="ml-2 text-[15px] font-semibold text-gray-700">网页预览</span>
            </div>
            <div className="flex-1 overflow-y-auto px-10 py-10 md:px-16 no-scrollbar">
                <div className="mx-auto max-w-4xl">
                    {htmlContent ? (
                        <div
                            className="prose prose-slate max-w-none prose-img:rounded-xl"
                            dangerouslySetInnerHTML={{ __html: htmlContent }}
                            style={{ lineHeight: '1.8', fontSize: '16px' }}
                        />
                    ) : (
                        <div className="flex items-center justify-center py-20 text-sm text-gray-300">暂无内容</div>
                    )}
                </div>
            </div>
        </div>
    );

    const isMobile = deviceMode === 'mobile';

    const renderPreview = () => {
        if (channel === 'wechat') return isMobile ? renderWechatMobile() : renderWechatWeb();
        if (channel === 'xiaohongshu') return isMobile ? renderXHSMobile() : renderXHSWeb();
        return renderWordPressWeb();
    };

    return ReactDOM.createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center overflow-hidden">
            {/* Dark Blur Overlay Backdrop */}
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity" onClick={onClose} />

            {/* Top Floating Glassmorphism Controls */}
            <div className="absolute top-6 left-1/2 -translate-x-1/2 z-[110] flex items-center gap-4 animate-fade-in-down">
                {/* Device Toggle Slider */}
                <div className="flex items-center rounded-full bg-black/40 backdrop-blur-xl p-1.5 ring-1 ring-white/10 shadow-lg relative">
                    <div
                        className={`absolute top-1.5 bottom-1.5 w-[86px] rounded-full bg-white transition-transform duration-300 ease-in-out ${isMobile ? 'translate-x-[0px]' : 'translate-x-[90px]'}`}
                    />
                    <button
                        type="button"
                        onClick={() => setDeviceMode('mobile')}
                        className={`relative z-10 flex w-[86px] items-center justify-center gap-1.5 rounded-full px-3 py-1.5 text-[13px] font-bold transition-colors duration-300 ${deviceMode === 'mobile' ? 'text-gray-900' : 'text-white/70 hover:text-white'}`}
                    >
                        <Smartphone className="h-4 w-4" /> 手机
                    </button>
                    <button
                        type="button"
                        onClick={() => setDeviceMode('web')}
                        className={`relative z-10 flex w-[86px] items-center justify-center gap-1.5 rounded-full px-3 py-1.5 text-[13px] font-bold transition-colors duration-300 ${deviceMode === 'web' ? 'text-gray-900' : 'text-white/70 hover:text-white'}`}
                    >
                        <Monitor className="h-4 w-4" /> 桌面
                    </button>
                </div>
            </div>

            {/* Top Right Close Button */}
            <button
                type="button"
                onClick={onClose}
                className="absolute top-6 right-6 z-[110] flex h-11 w-11 items-center justify-center rounded-full bg-black/40 backdrop-blur-xl text-white/70 ring-1 ring-white/10 shadow-lg transition-all hover:bg-black/60 hover:text-white hover:scale-105"
            >
                <X className="h-6 w-6 stroke-[2.5px]" />
            </button>

            {/* Main Content Render Area - Direct View without wrapping shell */}
            <div
                className={`flex h-[88vh] items-center justify-center z-[105] relative transition-all duration-500 ease-[cubic-bezier(0.3,0.8,0.2,1)] will-change-[width,max-width] pt-8
                    ${isMobile
                        ? (channel === 'xiaohongshu' ? 'w-full max-w-[760px] pt-12' : 'w-full max-w-[420px]')
                        : 'w-full max-w-[1100px]'}
                `}
            >
                {renderPreview()}
            </div>
        </div>,
        document.body
    );
};

export default ContentPreviewModal;
